import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import { Chip, Stack } from "@mui/material";
import clsx from "clsx";
import { isSameDay } from "date-fns";
import { type MouseEvent, type Ref, forwardRef } from "react";
import type { ICalendarCellProps } from "./cell";
import { CalendarCellIcon } from "./cellIcon";
import { classes } from "./styles";
import type { ICalendarCell, TCalendarEventRange } from "./types";

export interface ICalendarEventProps<T = undefined> {
  cell: ICalendarCell<T>;
  event: TCalendarEventRange<T>;
  overflow: boolean;
  onEventClick?: ICalendarCellProps<T>["onEventClick"];
}

export const CalendarEvent = forwardRef(function CalendarEvent<T>(
  { cell, event, overflow, onEventClick }: ICalendarEventProps<T>,
  ref: Ref<HTMLDivElement>
) {
  return (
    <Chip
      className={clsx(classes.event, {
        [classes.eventStart]: isSameDay(event.startRange, cell.date) && !isSameDay(event.endRange, cell.date),
        [classes.eventDuring]: !isSameDay(event.startRange, cell.date) && !isSameDay(event.endRange, cell.date),
        [classes.eventEnd]: !isSameDay(event.startRange, cell.date) && isSameDay(event.endRange, cell.date),
        [classes.eventOverflow]: overflow,
      })}
      variant={event.variant}
      color={event.color}
      sx={event.sx}
      size="small"
      label={
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1,
          }}
        >
          {event.icon && <CalendarCellIcon icon={event.icon} />}
          <TypoTooltip variant="body2SemiBold" noWrap>
            {event.children.length > 0 && `(${event.children.length + 1})`} {event.label}
          </TypoTooltip>
        </Stack>
      }
      ref={ref}
      {...(onEventClick && {
        onClick: (evt: MouseEvent<HTMLDivElement>) => onEventClick(evt, event, cell.events),
      })}
    />
  );
});
